import { useState, useEffect, useCallback } from 'react';
import type { CalendarEvent, AttendanceRecord, DrivingAssignment } from '../types';
import * as api from '../api/client';
import { getToday } from '../utils/helpers';

export function useTodayEvent() {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [nextEvent, setNextEvent] = useState<CalendarEvent | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [driving, setDriving] = useState<DrivingAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getToday();
      setEvent(data.event);
      setNextEvent(data.nextEvent || null);
      setAttendance(data.attendance || []);
      setDriving(data.driving || null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { event, nextEvent, attendance, driving, loading, error, refresh };
}

export function useWeekEvents() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const today = new Date();
      const dayOfWeek = today.getDay();
      const start = new Date(today);
      start.setDate(today.getDate() - dayOfWeek);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const fromStr = start.toISOString().split('T')[0];
      const toStr = end.toISOString().split('T')[0];
      const data = await api.getEvents(fromStr, toStr);
      setEvents(data.events);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { events, loading, error, refresh };
}

export function useEventDetail(eventId: string) {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [driving, setDriving] = useState<DrivingAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getEvent(eventId);
      setEvent(data.event);
      setAttendance(data.attendance);
      setDriving(data.driving);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { event, attendance, driving, loading, error, refresh };
}
